import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { buttonStyle } from 'components/buttons/buttonStyle';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput.tsx';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput.tsx';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import { ModalFooter } from 'components/modals/ModalFooter.tsx';
import { ModalHeader } from 'components/modals/ModalHeader.tsx';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features.ts';
import { t } from 'utils/getTranslations.ts';

interface TimecardFilterForm {
    employees: { id: number; name: string }[];
    matterAssignment: MatterAssignmentOption | null;
    timecardDateFrom: string | null;
    timecardDateTo: string | null;
    billable: boolean;
    nonBillable: boolean;
}

interface Props<T extends Timecard = Timecard> {
    onClose: () => void;
    onBulkSelect: (ids: number[]) => void;
    onBulkUnselect: (ids: number[]) => void;
    timecards: T[];
}

type MatterAssignmentOption = {
    id: number | string;
    name: string;
};

interface Timecard {
    id: number;
    employee?: { id: number; name: string };
    matterAssignment?: { id: number; name: string };
    timecardDate?: string;
    isNonBillable?: boolean;
    isSelectable: boolean;
}

const defaultValues: TimecardFilterForm = {
    employees: [],
    matterAssignment: null,
    timecardDateFrom: null,
    timecardDateTo: null,
    billable: false,
    nonBillable: false,
};

const TimecardsBulkSelectModal = <T extends Timecard = Timecard>({
    onClose,
    onBulkSelect,
    onBulkUnselect,
    timecards,
}: Props<T>) => {
    const isNew = useIsNewDesign();
    const { handleSubmit, control, setValue } = useForm<TimecardFilterForm>({
        defaultValues,
    });

    const [
        employeesControlValue,
        matterAssignmentControlValue,
        timecardDateFromControlValue,
        timecardDateToControlValue,
        billableControlValue,
        nonBillableControlValue,
    ] = useWatch({
        control,
        name: ['employees', 'matterAssignment', 'timecardDateFrom', 'timecardDateTo', 'billable', 'nonBillable'],
    });

    const employeeOptions = useMemo(() => {
        const uniqueMap = new Map<number, string>();
        timecards.forEach((tc) => {
            if (tc.employee) uniqueMap.set(tc.employee.id, tc.employee.name);
        });
        return Array.from(uniqueMap.entries()).map(([id, name]) => ({ id, name }));
    }, [timecards]);

    const assignmentOptions = useMemo(() => {
        const uniqueMap = new Map<number, string>();
        timecards.forEach((tc) => {
            const ma = tc.matterAssignment;
            if (ma) uniqueMap.set(ma.id, ma.name);
        });
        const options: MatterAssignmentOption[] = Array.from(uniqueMap.entries()).map(([id, name]) => ({ id, name }));
        if (options.length > 0 && timecards.some((tc) => tc.matterAssignment == null)) {
            options.push({ id: 'NO_ASSIGNMENT', name: t('invoices.bulk-select-timecards-modal.without-assignment') });
        }
        return options;
    }, [timecards]);

    const filterItems = ({
        employees,
        matterAssignment,
        timecardDateFrom,
        timecardDateTo,
        billable,
        nonBillable,
    }: TimecardFilterForm) => {
        return timecards
            .filter((tc) => tc.isSelectable)
            .filter((tc) => {
                const byEmployee =
                    employees.length == 0 || employees.some((employee) => employee.id === tc.employee?.id);

                let byAssignment = true;
                if (matterAssignment) {
                    if (matterAssignment.id === 'NO_ASSIGNMENT') {
                        byAssignment = tc.matterAssignment == null;
                    } else {
                        byAssignment = tc.matterAssignment?.id === matterAssignment.id;
                    }
                }

                let byDateRange = true;
                if (timecardDateFrom || timecardDateTo) {
                    const timecardDate = tc.timecardDate;
                    if (timecardDate) {
                        if (timecardDateFrom && timecardDate < timecardDateFrom) {
                            byDateRange = false;
                        }
                        if (timecardDateTo && timecardDate > timecardDateTo) {
                            byDateRange = false;
                        }
                    } else {
                        byDateRange = false;
                    }
                }

                const byBillability = billable === nonBillable || (billable ? !tc.isNonBillable : !!tc.isNonBillable);

                return byEmployee && byAssignment && byDateRange && byBillability;
            });
    };

    const onBulkSelectClick = (isToSelect: boolean) => {
        handleSubmit((form) => {
            const result = filterItems(form).map((tc) => tc.id);
            if (isToSelect) {
                onBulkSelect(result);
            } else {
                onBulkUnselect(result);
            }
        })();
        onClose();
    };

    const matchCount = (
        <FormattedMessage
            id="invoices.bulk-select-timecards-modal.match-count"
            values={{
                count: filterItems({
                    employees: employeesControlValue,
                    matterAssignment: matterAssignmentControlValue,
                    timecardDateFrom: timecardDateFromControlValue,
                    timecardDateTo: timecardDateToControlValue,
                    billable: billableControlValue,
                    nonBillable: nonBillableControlValue,
                }).length,
            }}
        />
    );
    const unselect = <FormattedMessage id="invoices.bulk-select-timecards-modal.bulk-unselect-button-label" />;

    return (
        <>
            <ModalHeader
                kit
                title={t('invoices.bulk-select-timecards-modal.title')}
                description={
                    <FormattedMessage
                        id="invoices.bulk-select-timecards-modal.description"
                        values={{ count: timecards.length }}
                    />
                }
                onCancel={onClose}
            />
            <div className="new:px-6 new:py-6 h-full overflow-y-auto px-8 py-[38px]">
                <div className="flex flex-col gap-4">
                    {/* The new design says this in the header's description. */}
                    <p className="text-body1 new:hidden">
                        <FormattedMessage
                            id="invoices.bulk-select-timecards-modal.description"
                            values={{ count: timecards.length }}
                        />
                    </p>
                    <MultiSelectInput
                        control={control}
                        setValue={setValue}
                        label="forms.select-employee"
                        name="employees"
                        options={employeeOptions}
                        getOptionIdentifier="id"
                        getOptionLabel="name"
                    />
                    {assignmentOptions.length > 0 && (
                        <FeatureFlagged action="show" flag={FeatureFlag.CASE_ASSIGNMENT_ENABLED}>
                            <SelectInput
                                control={control}
                                setValue={setValue}
                                label="forms.select-matter-assignment"
                                name="matterAssignment"
                                options={assignmentOptions}
                                getOptionIdentifier="id"
                                getOptionLabel="name"
                            />
                        </FeatureFlagged>
                    )}
                    <div className="flex flex-col gap-4 md:flex-row">
                        <DateInput name="timecardDateFrom" control={control} label="date-from" fullWidth />
                        <DateInput name="timecardDateTo" control={control} label="date-to" fullWidth />
                    </div>
                    <div className="flex gap-6">
                        <CheckboxInput
                            control={control}
                            name="billable"
                            label="invoices.bulk-select-timecards-modal.billable"
                        />
                        <CheckboxInput
                            control={control}
                            name="nonBillable"
                            label="invoices.bulk-select-timecards-modal.non-billable"
                        />
                    </div>
                    {/* The new design says it in the footer, beside the buttons it counts for. */}
                    <p className="text-body1 new:hidden">{matchCount}</p>
                </div>
            </div>
            <ModalFooter
                kit
                onCancel={onClose}
                onSave={() => onBulkSelectClick(true)}
                saveButtonTextId={'invoices.bulk-select-timecards-modal.bulk-select-button-label'}
                // The new design keeps one amber answer: Bulk unselect is outlined, beside Cancel, the count before them.
                note={isNew ? <span role="status">{matchCount}</span> : undefined}
                extraButton={
                    isNew ? undefined : (
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary', size: 'small' })}
                            onClick={() => onBulkSelectClick(false)}
                        >
                            {unselect}
                        </Button>
                    )
                }
                secondaryButton={
                    isNew ? (
                        <Button variant="outline" onClick={() => onBulkSelectClick(false)}>
                            {unselect}
                        </Button>
                    ) : undefined
                }
            />
        </>
    );
};

export default TimecardsBulkSelectModal;
